import { useEffect, useState } from 'react';

/** Breathing room kept between the menu and the edge of the visible area. */
const EDGE_MARGIN_PX = 8;

/**
 * Measures how much of the screen is free for a dropdown menu on its side of
 * the control, using the visual viewport so a tablet's on-screen keyboard
 * counts as covered.
 *
 * react-select sizes its menu once, when it opens - before the keyboard
 * slides up - so without a cap, a menu opened above the input runs off the
 * top of the screen in landscape and its first options can't be reached.
 *
 * @param {boolean} isOpen - Whether the menu is open; nothing is measured otherwise.
 * @param {Function} getControl - Returns the control element the menu hangs from.
 * @param {Function} getMenu - Returns the rendered menu element.
 * @returns {number|null} Pixels available for the menu, or null when unknown.
 */
const useVisibleMenuSpace = (isOpen, getControl, getMenu) => {
  const [space, setSpace] = useState(null);

  useEffect(() => {
    const viewport = typeof window === 'object' ? window.visualViewport : null;
    if (!isOpen || !viewport) {
      setSpace(null);
      return undefined;
    }

    const measure = () => {
      const control = getControl();
      const menu = getMenu();
      if (!control?.getBoundingClientRect || !menu?.getBoundingClientRect) return;
      const controlRect = control.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      /** Client rects are relative to the layout viewport, which starts at
       * scrollY; the visible area starts at the visual viewport's pageTop.
       * (When the keyboard opens, iOS moves both, so offsetTop alone would
       * count the keyboard's scroll twice.) */
      const visibleTop = viewport.pageTop - window.scrollY;
      const visibleBottom = visibleTop + viewport.height;
      const isAbove = menuRect.top < controlRect.top;
      const available = isAbove
        ? controlRect.top - visibleTop
        : visibleBottom - controlRect.bottom;
      setSpace(Math.floor(available - EDGE_MARGIN_PX));
    };

    /** The menu mounts on the same commit as the picker opens; wait a frame
     * so it has a position to measure. */
    const frame = requestAnimationFrame(measure);
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    /** Any scroll can move the control - the page lifting it above the
     * keyboard, or the calendar list scrolling under it. Re-measuring also
     * re-renders the picker, which re-anchors react-select's menu to it.
     * Scrolling the menu's own results moves nothing, so it's skipped. */
    const measureOnScroll = (e) => {
      if (e.target instanceof Node && getMenu()?.contains?.(e.target)) return;
      measure();
    };
    window.addEventListener('scroll', measureOnScroll, true);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
      window.removeEventListener('scroll', measureOnScroll, true);
    };
    // Getters read refs, so they don't need to retrigger the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return space;
};

export default useVisibleMenuSpace;
